import { useState } from 'react'
import { Mtds, type SampleTakingPlannedEndpoint } from 'prove-common'
import type { CumulativeChartRow } from './statisticsTypes'
import { getCumulativeReportCutoff } from '../../utils/statisticsUtils'
import type { ChartElementRef } from './statisticsChartUtils'
import { useChartLineCutoff } from './useChartLineCutoff'

const monthLabels = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'Mai',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Okt',
  'Nov',
  'Des'
]

const formatChartDate = (date: Date) =>
  date.toLocaleDateString('nb-NO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC'
  })

// Inclusive reporting dates are drawn at the boundary after that day's activity.
const endOfDay = (date: Date) =>
  new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + 1)
  )

export const CumulativeChart = ({
  rows,
  year,
  showPlanned = true,
  plannedEndpoint
}: {
  rows: CumulativeChartRow[]
  year: number
  showPlanned?: boolean
  plannedEndpoint?: SampleTakingPlannedEndpoint
}) => {
  const [chart, setChart] = useState<ChartElementRef | null>(null)
  const chartRows = rows.length
    ? [
        {
          date: new Date(Date.UTC(year, 0, 1)),
          cumulativePlanned: 0,
          cumulativeTaken: 0
        },
        ...rows.map((row) => ({
          ...row,
          date: new Date(Date.UTC(year, row.id + 1, 1))
        }))
      ]
    : []
  const lastBoundary = chartRows.at(-1)?.date
  const cutoff = new Date(
    Math.min(
      new Date(`${getCumulativeReportCutoff(year)}T00:00:00Z`).getTime(),
      lastBoundary ? lastBoundary.getTime() - 86400000 : Infinity
    )
  )
  const takenBoundary = endOfDay(cutoff)
  const takenBoundaryIndex = chartRows.findIndex(
    (row) => row.date >= takenBoundary
  )
  const lastTakenIndex =
    takenBoundaryIndex < 0 ? chartRows.length - 1 : takenBoundaryIndex
  const hasSamples = chartRows.some(
    (row, index) => index <= lastTakenIndex && (row.cumulativeTaken ?? 0) > 0
  )
  const due = plannedEndpoint
    ? new Date(`${plannedEndpoint.date}T00:00:00Z`)
    : undefined
  const dueBoundaryIndex = due
    ? chartRows.findIndex((row) => row.date >= due)
    : -1
  const lastPlannedIndex =
    dueBoundaryIndex < 0 ? chartRows.length - 1 : dueBoundaryIndex
  const plannedValues = chartRows.map((row, index) =>
    index === lastPlannedIndex && plannedEndpoint
      ? plannedEndpoint.cumulativePlannedUnitCount
      : row.cumulativePlanned
  )
  const getEndPosition = (lastIndex: number, boundary: Date) =>
    lastIndex > 0
      ? Math.max(
          0,
          Math.min(
            lastIndex,
            lastIndex -
              1 +
              (boundary.getTime() - chartRows[lastIndex - 1].date.getTime()) /
                (chartRows[lastIndex].date.getTime() -
                  chartRows[lastIndex - 1].date.getTime())
          )
        )
      : lastIndex
  const plannedCutoff =
    showPlanned && due
      ? {
          lastIndex: lastPlannedIndex,
          endPosition: getEndPosition(lastPlannedIndex, due),
          values: plannedValues,
          label: `Per ${formatChartDate(due)}`
        }
      : undefined
  const takenCutoff = {
    lastIndex: lastTakenIndex,
    endPosition: getEndPosition(lastTakenIndex, takenBoundary),
    values: chartRows.map((row) => row.cumulativeTaken ?? 0),
    label: `Til og med ${formatChartDate(cutoff)}`
  }
  const axisLabels = chartRows.map((row) =>
    row.date.getUTCFullYear() === year
      ? monthLabels[row.date.getUTCMonth()]
      : ''
  )
  useChartLineCutoff(chart, takenCutoff, showPlanned, axisLabels, plannedCutoff)
  const series = [
    ...(showPlanned
      ? [{ name: 'Planlagt', values: plannedValues, cutoff: plannedCutoff }]
      : []),
    ...(hasSamples
      ? [{ name: 'Tatt', values: takenCutoff.values, cutoff: takenCutoff }]
      : [])
  ]

  return (
    <Mtds.Chart
      ref={setChart}
      aria-label={`Akkumulert uttak (${year})`}
      data-variant='line'
      className='overview-plan-progress-chart'
    >
      <table>
        <thead>
          <tr>
            <th scope='col'>Måned</th>
            {chartRows.map((row, index) => (
              <th scope='col' key={row.date.toISOString()}>
                {index === 0
                  ? 'Årsstart'
                  : monthLabels[
                      new Date(row.date.getTime() - 86400000).getUTCMonth()
                    ]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {series.map(({ name, values, cutoff: seriesCutoff }) => (
            <tr key={name}>
              <th scope='row'>{name}</th>
              {chartRows.map((row, index) => {
                const hidden = seriesCutoff && index > seriesCutoff.lastIndex
                const dateLabel =
                  index === 0
                    ? `Ved årets start (${year})`
                    : seriesCutoff && index === seriesCutoff.lastIndex
                      ? seriesCutoff.label
                      : `Til og med ${formatChartDate(new Date(row.date.getTime() - 86400000))}`
                const tooltip = hidden
                  ? undefined
                  : `${name}: ${values[index]} (${dateLabel})`
                return (
                  <td
                    key={row.date.toISOString()}
                    data-tooltip={tooltip}
                    aria-label={tooltip}
                  >
                    {hidden ? '' : values[index]}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Mtds.Chart>
  )
}
